import React, { useState, useEffect } from 'react';
import { leaseApi, rouFinAlyzerApi } from '../services/api';
import UploadLeaseModal from './UploadLeaseModal';
import GenerateLeaseAnalysisModal from './GenerateLeaseAnalysisModal';
import '../styles/Panel.css';

function LeaseManagementPanel({ entity, onComplete }) {
  const [leaseStatus, setLeaseStatus] = useState(null);
  const [outputs, setOutputs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);

  // Per-row push state keyed by output.timestamp
  const [pushState, setPushState] = useState({});  // { [timestamp]: { loading, success, error } }

  useEffect(() => {
    if (entity) {
      fetchLeaseStatus();
      fetchOutputs();
    }
  }, [entity]);

  const fetchLeaseStatus = async () => {
    try {
      setLoading(true);
      const data = await leaseApi.getLeaseStatus(entity.name);
      setLeaseStatus(data);
    } catch (err) {
      console.error('Failed to fetch lease status:', err);
      setError('Failed to load lease status');
    } finally {
      setLoading(false);
    }
  };

  const fetchOutputs = async () => {
    try {
      const data = await leaseApi.listLeaseOutputs(entity.name);
      setOutputs(data.outputs || []);
    } catch (err) {
      console.error('Failed to fetch lease outputs:', err);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setError(null);
      const blob = await leaseApi.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'lease_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download template: ' + err.message);
    }
  };

  const handleDownloadLeaseData = async () => {
    try {
      setError(null);
      const blob = await leaseApi.downloadLeaseData(entity.name);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lease_input_${entity.name}_${leaseStatus.latest_version.timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err){
      setError('Failed to download lease data: ' + err.message);
    }
  };

  const handleDownloadOutput = async (output) => {
    try {
      setError(null);
      // Extract start_date and end_date from output metadata or parse from period
      const startDate = output.metadata?.start_date || output.period.split('_to_')[0];
      const endDate = output.metadata?.end_date || output.period.split('_to_')[1];

      const blob = await leaseApi.downloadLeaseOutput(entity.name, startDate, endDate, output.timestamp);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lease_analysis_${entity.name}_${output.period}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download output: ' + err.message);
    }
  };

  const handlePushToFinalyzer = async (output) => {
    const key = output.timestamp;
    const endDate = output.end_date || output.period.split('_to_')[1];
    setPushState(prev => ({ ...prev, [key]: { loading: true, success: false, error: null } }));
    try {
      const res = await rouFinAlyzerApi.push(entity.name, endDate);
      if (!res.success) throw new Error(res.error || 'Push failed');
      setPushState(prev => ({ ...prev, [key]: { loading: false, success: true, records: res.records_pushed, error: null } }));
    } catch (err) {
      setPushState(prev => ({ ...prev, [key]: { loading: false, success: false, error: err.message || 'Push failed' } }));
    }
  };

  const handleUploadComplete = () => {
    setShowUploadModal(false);
    fetchLeaseStatus();
    if (onComplete) onComplete();
  };

  const handleAnalysisComplete = () => {
    setShowAnalysisModal(false);
    fetchOutputs();
    if (onComplete) onComplete();
  };

  if (loading && !leaseStatus) {
    return (
      <div className="panel lease-panel">
        <h2>ROU & Lease Liability</h2>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="panel lease-panel">
      <h2>ROU & Lease Liability</h2>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Action Buttons */}
      <div className="button-group">
        <button
          className="btn btn-secondary"
          onClick={handleDownloadTemplate}
        >
          Download Template
        </button>
        <button
          className="btn btn-primary"
          onClick={() => setShowUploadModal(true)}
        >
          Upload Lease Data
        </button>
      </div>

      {/* Lease Data Section */}
      {leaseStatus && leaseStatus.has_data ? (
        <div className="section">
          <h3>Latest Lease Data</h3>
          <div className="info-card">
            <div className="info-row">
              <span className="info-label">Uploaded:</span>
              <span className="info-value">
                {new Date(leaseStatus.latest_version.upload_time).toLocaleString()}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Total Leases:</span>
              <span className="info-value">{leaseStatus.latest_version.lease_count}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Lease IDs:</span>
              <span className="info-value">
                {leaseStatus.latest_version.lease_ids?.join(', ') || 'N/A'}
              </span>
            </div>
          </div>
          <div className="button-group">
            <button
              className="btn btn-secondary"
              onClick={handleDownloadLeaseData}
            >
              Download Data
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowAnalysisModal(true)}
            >
              Generate Analysis
            </button>
          </div>
        </div>
      ) : (
        <div className="section">
          <div className="alert alert-info">
            No lease data available. Please upload lease data to get started.
          </div>
        </div>
      )}

      {/* Analysis Outputs Section */}
      {outputs.length > 0 && (
        <div className="section">
          <h3>Previous Analyses</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Generated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {outputs.map((output, idx) => {
                const ps = pushState[output.timestamp] || {};
                return (
                  <tr key={idx}>
                    <td>{output.period}</td>
                    <td>{output.start_date || 'N/A'}</td>
                    <td>{output.end_date || 'N/A'}</td>
                    <td>
                      {output.upload_time
                        ? new Date(output.upload_time).toLocaleString()
                        : 'N/A'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleDownloadOutput(output)}
                        style={{ marginRight: '8px' }}
                      >
                        Download
                      </button>
                      {ps.success ? (
                        <button className="btn btn-sm" disabled
                          style={{ background: '#4caf50', color: '#fff', border: 'none' }}>
                          Pushed ✓ ({ps.records})
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handlePushToFinalyzer(output)}
                          disabled={ps.loading}
                          title="Push Finalyzer Input tab to FinAlyzer"
                        >
                          {ps.loading ? 'Pushing...' : 'Push to FinAlyzer'}
                        </button>
                      )}
                      {ps.error && (
                        <div style={{ fontSize: '11px', color: '#c62828', marginTop: '4px', maxWidth: '220px', whiteSpace: 'normal' }}>
                          {ps.error}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showUploadModal && (
        <UploadLeaseModal
          entity={entity}
          onClose={() => setShowUploadModal(false)}
          onComplete={handleUploadComplete}
        />
      )}

      {showAnalysisModal && (
        <GenerateLeaseAnalysisModal
          entity={entity}
          onClose={() => setShowAnalysisModal(false)}
          onComplete={handleAnalysisComplete}
        />
      )}
    </div>
  );
}

export default LeaseManagementPanel;
